import {
  CreatePipefyBuildStudioPlanCardInput,
  EmptySurveyQuestionResponseError,
  EmptySurveyResponseError,
  InvalidSurveyQuestionError,
  InvalidSurveyQuestionResponseError,
  InvalidSurveyResponseError,
  PackageRecommendationOutput,
  SubmitQuizToPipefyInput,
  SurveyQuestionResponse,
  SurveyQuestionResponseSchema,
  SurveyQuestionSchema,
  SurveyResponse,
  SurveyResponseInput,
  SurveyResponseSchema,
  UpsertSurveyQuestionResponseInput,
  UpsertSurveyQuestionResponsesInput,
  UpsertSurveyQuestionResponsesOutput,
} from "@yardzen/api-dtos";
import { AxiosError, AxiosResponse } from "axios";
import { z } from "zod";
import { apiClient } from "../base-client";
import { createServerApiClient } from "../server-base-client";

const getApiClient = async (authToken?: string) => {
  if (authToken) {
    return await createServerApiClient(authToken);
  }
  return apiClient;
};

export async function getOrCreateSurveyResponse(
  accountId: string,
  surveyId: string,
  { buildProjectId, userId }: SurveyResponseInput,
  authToken?: string,
): Promise<AxiosResponse<SurveyResponse>> {
  const apiClient = await getApiClient(authToken);

  return await apiClient.post(
    `surveys/${surveyId}/survey-responses/findOrCreate`,
    {
      buildProjectId,
      accountId,
      userId,
    },
  );
}

export async function upsertSurveyQuestionResponses(
  accountId: string | undefined,
  buildProjectId: string | undefined,
  surveyResponseId: string,
  surveyId: string,
  { questionResponses, userId }: UpsertSurveyQuestionResponsesInput,
  isPublic: boolean,
  designProjectId?: string | undefined,
  authToken?: string,
): Promise<AxiosResponse<UpsertSurveyQuestionResponsesOutput>> {
  const apiClient = await getApiClient(authToken);
  return await apiClient.post(
    `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}/survey-question-responses/upsert`,
    { questionResponses, userId, buildProjectId, accountId, designProjectId },
  );
}

export async function findSurveyResponse(
  surveyId: string,
  surveyResponseId: string,
  isPublic = false,
  authToken?: string,
): Promise<AxiosResponse<SurveyResponse>> {
  const apiClient = await getApiClient(authToken);
  return await apiClient.get(
    `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}`,
  );
}

export async function findSurveyResponseByAccountId(
  accountId: string,
  surveyId: string,
  authToken: string,
): Promise<AxiosResponse<SurveyResponse>> {
  const apiClient = await getApiClient(authToken);
  return await apiClient.get(
    `surveys/${surveyId}/survey-responses/account/${accountId}`,
  );
}

export async function findRecommendedPackage(
  surveyId: string,
  surveyResponseId: string,
  variant: "good-better-best" | "default",
): Promise<AxiosResponse<PackageRecommendationOutput>> {
  return await apiClient.get(
    `surveys/${surveyId}/public/survey-responses/${surveyResponseId}/recommended-package?variant=${variant}`,
  );
}

export async function createSurveyResponse(
  surveyId: string,
  isPublic = false,
  authToken?: string,
): Promise<AxiosResponse<SurveyResponse>> {
  const apiClient = await getApiClient(authToken);
  const surveyResponse = await apiClient.post(
    `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}`,
  );

  return surveyResponse;
}

export async function updateSurveyResponse(
  surveyId: string,
  surveyResponseId: string,
  input: SurveyResponseInput,
  isPublic: boolean,
  authToken?: string,
) {
  const apiClient = await getApiClient(authToken);
  try {
    const response = await apiClient.put(
      `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}`,
      input,
    );

    if (!response.data) {
      throw new EmptySurveyResponseError();
    }

    return validateSurveyResponseData(response.data, surveyId);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptySurveyResponseError();
    }
    throw error;
  }
}

export async function upsertSurveyQuestionResponse(
  surveyId: string,
  surveyResponseId: string,
  questionResponses: UpsertSurveyQuestionResponseInput[],
  isPublic: boolean,
  authToken?: string,
): Promise<AxiosResponse<UpsertSurveyQuestionResponsesOutput>> {
  const apiClient = await getApiClient(authToken);
  const response = await apiClient.post(
    `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}/survey-question-responses/upsert`,
    { questionResponses },
  );

  return response;
}

export async function getSurveyQuestionResponse(
  surveyId: string,
  surveyResponseId: string,
  questionId: string,
  isPublic: boolean,
  authToken?: string,
): Promise<SurveyQuestionResponse> {
  const apiClient = await getApiClient(authToken);
  try {
    const url = `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}/survey-question/${questionId}`;

    const response = await apiClient.get(url, {
      headers: {
        "Cache-Control": "no-store",
      },
    });

    if (!response.data) {
      throw new EmptySurveyQuestionResponseError();
    }

    return validateSurveyQuestionResponseData(response.data, surveyId);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new EmptySurveyQuestionResponseError();
    }
    throw error;
  }
}

export async function findPreviousQuestion(
  surveyId: string,
  surveyResponseId: string,
  surveyQuestionId: string,
  isPublic: boolean,
  authToken?: string,
) {
  const apiClient = await getApiClient(authToken);
  try {
    const response = await apiClient.get(
      `surveys/${surveyId}/${isPublic ? "public/survey-responses" : "survey-responses"}/${surveyResponseId}/survey-question/${surveyQuestionId}/previous`,
      {
        headers: {
          "Cache-Control": "no-store",
        },
      },
    );

    if (response.data)
      return validateSurveyQuestionData(response.data, surveyId);

    return null;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function findOrCreatePipefyBuildStudioPlanCard(
  surveyId: string,
  accountId: string,
  {
    userId,
    buildProjectId,
    accountManagerEmail,
  }: CreatePipefyBuildStudioPlanCardInput,
): Promise<AxiosResponse> {
  return await apiClient.post(
    `surveys/${surveyId}/survey-responses/find-or-create-pipefy-build-studio-plan-card`,
    {
      accountId,
      userId,
      buildProjectId,
      accountManagerEmail,
    },
  );
}

export async function submitQuizToPipefy(
  surveyId: string,
  accountId: string,
  input: SubmitQuizToPipefyInput,
): Promise<AxiosResponse> {
  return await apiClient.post(
    `surveys/${surveyId}/survey-responses/submit-quiz-to-pipefy`,
    {
      accountId,
      ...input,
    },
  );
}

export async function findSurveyResponsesByAnonId(
  yzAnonymousId: string,
  authToken?: string,
): Promise<SurveyResponse[]> {
  try {
    const apiClient = await getApiClient(authToken);
    const response = await apiClient.get(
      `customer-leads/${yzAnonymousId}/survey-responses`,
    );

    if (!response.data) {
      return [];
    }

    // Validate each survey response in the array
    const surveyResponses = Array.isArray(response.data) ? response.data : [];
    return surveyResponses.map((data: unknown) =>
      validateSurveyResponseData(data, yzAnonymousId),
    ) as SurveyResponse[];
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
}

function validateSurveyResponseData(data: unknown, surveyKey: string) {
  try {
    return SurveyResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(
        `Invalid survey response for ${surveyKey}:`,
        JSON.stringify(data, null, 2),
      );
      throw new InvalidSurveyResponseError(error);
    }
    throw error;
  }
}

function validateSurveyQuestionResponseData(
  data: unknown,
  surveyKey: string,
): SurveyQuestionResponse {
  try {
    const parsed = SurveyQuestionResponseSchema.parse(data);
    if (
      !parsed.surveyResponseId ||
      !parsed.surveyQuestionId ||
      !parsed.response
    ) {
      throw new Error("Missing required fields");
    }
    return parsed as SurveyQuestionResponse;
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(
        `Invalid survey question response for ${surveyKey}:`,
        JSON.stringify(data, null, 2),
      );
      throw new InvalidSurveyQuestionResponseError(error);
    }
    throw error;
  }
}

function validateSurveyQuestionData(data: unknown, surveyKey: string) {
  try {
    return SurveyQuestionSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(
        `Invalid survey question for ${surveyKey}:`,
        JSON.stringify(data, null, 2),
      );
      throw new InvalidSurveyQuestionError(error);
    }
    throw error;
  }
}
